import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FindingDto } from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import {
  FINDING_LEVEL_TAGS,
  describeFindingSource,
  visibleFindings,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/**
 * Pontos de atenção (do sócio ou do grupo). De saída mostra os que o backend marcou como exibidos (até 4) e o
 * "+n críticos" que ficaram de fora; "Ver mais n pontos" abre a lista completa, com os dispensados.
 */
@Component({
  selector: 'app-pit-finding-list',
  standalone: true,
  imports: [VaultDateTimePipe],
  templateUrl: './finding-list.component.html',
  styleUrl: './finding-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindingListComponent {
  readonly findings = input.required<FindingDto[]>();
  readonly hiddenCriticalCount = input(0);
  readonly heading = input('Pontos de atenção');
  readonly subheading = input('Os pontos mais relevantes deste ano');
  /** Frase da lista vazia (o consolidado explica que os pontos de cada sócio estão na aba dele). */
  readonly emptyText = input('Nenhum ponto de atenção neste ano.');
  /** Pontos do grupo já vêm todos exibidos e não têm dispensa (a dispensa é por sócio e ano). */
  readonly allowExpand = input(true);
  /** `PARTNERINCOMETAX_UPDATE` e ponto de sócio. */
  readonly canDismiss = input(false);
  readonly busy = input(false);

  readonly dismiss = output<FindingDto>();
  readonly undoDismiss = output<FindingDto>();

  protected readonly showAll = signal(false);
  protected readonly levels = FINDING_LEVEL_TAGS;

  protected readonly shown = computed(() =>
    this.allowExpand() ? visibleFindings(this.findings(), this.showAll()) : this.findings()
  );

  /** Há mais achados que os exibidos de saída. */
  protected readonly hiddenCount = computed(
    () => this.findings().length - visibleFindings(this.findings(), false).length
  );

  protected readonly hasMore = computed(() => this.allowExpand() && this.hiddenCount() > 0);

  /** "Ver mais 1 ponto" / "Ver mais 3 pontos" (inclui os dispensados, que aparecem riscados). */
  protected readonly moreLabel = computed(() => {
    const count = this.hiddenCount();
    return `Ver mais ${count} ${count === 1 ? 'ponto' : 'pontos'}`;
  });

  protected readonly sourceOf = describeFindingSource;

  protected toggle(): void {
    this.showAll.update(value => !value);
  }
}
